import type { JSONContent } from "@tiptap/core";
import {
  type DocumentOptions,
  isMap,
  isScalar,
  isSeq,
  type Pair,
  type ParseOptions,
  parseDocument,
  Scalar,
  type SchemaOptions,
  stringify,
  type Node as YamlNode,
} from "yaml";

export type FrontMatterValueKind = "scalar" | "array" | "complex";
export type FrontMatterScalarType = "string" | "number" | "date" | "boolean" | "null";

export interface FrontMatterFieldAttrs {
  key: string;
  valueKind: FrontMatterValueKind;
  scalarType: FrontMatterScalarType | null;
  originalKey: string;
  originalCanonicalValue: string;
  originalSource: string;
}

export interface FrontMatterAttrs {
  trailingSource: string;
}

export interface FrontMatterParseResult {
  frontMatter: JSONContent;
  body: string;
}

export interface FrontMatterFieldError {
  fieldIndex: number;
  code: "duplicate-key" | "invalid-key" | "invalid-value";
  message: string;
}

export interface FrontMatterValidation {
  valid: boolean;
  errors: FrontMatterFieldError[];
}

export interface FrontMatterSerialization {
  yaml: string;
  valid: boolean;
  errors: FrontMatterFieldError[];
}

export const FRONT_MATTER_NODE_NAME = "frontMatter";
export const FRONT_MATTER_FIELD_NODE_NAME = "frontMatterField";

const OPENING_BOUNDARY = /^(?:\uFEFF)?---(?:\r?\n|$)/;
const CLOSING_BOUNDARY = /^---(?:\r?\n|$)/gm;

const SCHEMA_OPTIONS: SchemaOptions = { customTags: ["timestamp"] };

const PARSE_OPTIONS: ParseOptions & DocumentOptions & SchemaOptions = {
  ...SCHEMA_OPTIONS,
  keepSourceTokens: true,
  prettyErrors: false,
  strict: true,
  uniqueKeys: true,
};

interface FrontMatterSlice {
  yaml: string;
  body: string;
}

/** 문서 선두의 `---` 경계를 offset 기준으로 찾는다. 줄 단위 재조합은 하지 않는다. */
function frontMatterSlice(markdown: string): FrontMatterSlice | null {
  const opening = OPENING_BOUNDARY.exec(markdown);
  if (!opening) return null;
  CLOSING_BOUNDARY.lastIndex = opening[0].length;
  const closing = CLOSING_BOUNDARY.exec(markdown);
  if (!closing) return null;
  return {
    yaml: markdown.slice(opening[0].length, closing.index),
    body: markdown.slice(closing.index + closing[0].length),
  };
}

function lineStart(source: string, index: number): number {
  return source.lastIndexOf("\n", index - 1) + 1;
}

function lineEnd(source: string, index: number): number {
  if (index > 0 && source[index - 1] === "\n") return index;
  const next = source.indexOf("\n", index);
  return next === -1 ? source.length : next + 1;
}

/** 앞선 필드가 끝난 지점까지만 거슬러 올라가며 빈 줄과 주석 줄을 이 필드에 붙인다. */
function fieldSourceStart(source: string, keyStart: number, minStart: number): number {
  let start = lineStart(source, keyStart);
  while (start > minStart) {
    const previous = lineStart(source, start - 1);
    const line = source.slice(previous, start).trim();
    if (line !== "" && !line.startsWith("#")) break;
    start = previous;
  }
  return start;
}

type ParsedPair = Pair<YamlNode, YamlNode | null>;

function pairValueEnd(pair: ParsedPair): number {
  return pair.value?.range?.[1] ?? pair.key.range?.[1] ?? 0;
}

function classifyValue(
  value: YamlNode | null,
  rawSource: string
): {
  valueKind: FrontMatterValueKind;
  scalarType: FrontMatterScalarType | null;
} {
  if (value === null) return { valueKind: "scalar", scalarType: "null" };
  if (isScalar(value)) {
    if (value.type === Scalar.BLOCK_FOLDED || value.type === Scalar.BLOCK_LITERAL) {
      return { valueKind: "complex", scalarType: null };
    }
    if (value.value instanceof Date) {
      return /^\d{4}-\d{2}-\d{2}$/.test(rawSource.trim())
        ? { valueKind: "scalar", scalarType: "date" }
        : { valueKind: "scalar", scalarType: "string" };
    }
    if (value.value === null) return { valueKind: "scalar", scalarType: "null" };
    if (typeof value.value === "boolean") return { valueKind: "scalar", scalarType: "boolean" };
    if (typeof value.value === "number" || typeof value.value === "bigint") {
      return { valueKind: "scalar", scalarType: "number" };
    }
    return { valueKind: "scalar", scalarType: "string" };
  }
  if (
    isSeq(value) &&
    value.items.every((item) => isScalar(item) && typeof item.value === "string")
  ) {
    return { valueKind: "array", scalarType: null };
  }
  return { valueKind: "complex", scalarType: null };
}

function dateText(value: Date, rawSource: string): string {
  const raw = rawSource.trim();
  if (raw.length > 0 && Date.parse(raw) === value.getTime()) return raw;
  return value.toISOString();
}

function scalarText(value: Scalar, rawSource: string): string {
  if (value.value instanceof Date) return dateText(value.value, rawSource);
  if (value.value === null) return "";
  if (typeof value.value === "string") return value.value;
  return String(value.value);
}

/** complex 값의 노드 텍스트는 콜론 뒤 원문을 그대로 유지한다. */
function complexText(source: string, pair: ParsedPair, valueEnd: number): string {
  const keyEnd = pair.key.range?.[1] ?? 0;
  const colon = source.indexOf(":", keyEnd);
  const start = colon === -1 ? keyEnd : colon + 1;
  return source.slice(start, valueEnd).replace(/\r?\n$/, "");
}

function fieldText(field: JSONContent): string {
  return (field.content ?? []).map((child) => child.text ?? "").join("");
}

function initialFieldText(
  source: string,
  pair: ParsedPair,
  valueKind: FrontMatterValueKind,
  valueEnd: number
): string {
  const value = pair.value;
  if (valueKind === "complex") return complexText(source, pair, valueEnd);
  if (valueKind === "array" && isSeq(value)) {
    return stringify(value.toJSON(), { ...SCHEMA_OPTIONS, collectionStyle: "flow" }).trim();
  }
  if (value === null) return "";
  if (isScalar(value)) {
    const range = value.range;
    return scalarText(value, range ? source.slice(range[0], range[1]) : "");
  }
  return "";
}

type FieldValueResult =
  | { ok: true; canonical: string; render: (key: string) => string }
  | { ok: false; message: string };

function parseFlowSequence(text: string): unknown[] | null {
  const document = parseDocument(text, PARSE_OPTIONS);
  if (document.errors.length > 0) return null;
  const contents = document.contents;
  if (!isSeq(contents) || !contents.items.every((item) => isScalar(item))) return null;
  return contents.toJSON() as unknown[];
}

/**
 * 단순 배열 필드의 노드 텍스트를 태그 목록으로 읽는다.
 * flow sequence가 아니거나 YAML 오류이면 `null`을 돌려주어 호출자가 원문을 보존하게 한다.
 */
export function parseFrontMatterTags(text: string): string[] | null {
  const items = parseFlowSequence(text);
  if (items === null || !items.every((item) => typeof item === "string")) return null;
  return items;
}

/** 태그 목록을 단순 배열 필드의 노드 텍스트 표현으로 되돌린다. */
export function serializeFrontMatterTags(tags: string[]): string {
  return stringify(tags, { ...SCHEMA_OPTIONS, collectionStyle: "flow" }).trim();
}

/**
 * 노드 텍스트를 boolean 필드의 값으로 읽는다. `true`/`false` 외의 표현은 `null`을 돌려주어
 * 호출자가 오류로 다루거나 원문을 그대로 보존하게 한다.
 */
export function parseFrontMatterBoolean(text: string): boolean | null {
  const normalized = text.trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return null;
}

/** 유효한 scalar 값 하나를 표준 YAML로 출력하는 결과를 만든다. */
function scalarValue(canonical: string, value: unknown): FieldValueResult {
  return { ok: true, canonical, render: (key) => stringify({ [key]: value }) };
}

function parseScalarText(scalarType: FrontMatterScalarType, text: string): FieldValueResult {
  switch (scalarType) {
    case "number": {
      const trimmed = text.trim();
      const value = Number(trimmed);
      if (trimmed === "" || Number.isNaN(value) !== (trimmed === "NaN")) {
        return { ok: false, message: "숫자 값이 아닙니다." };
      }
      return scalarValue(String(value), value);
    }
    case "boolean": {
      const value = parseFrontMatterBoolean(text);
      if (value === null) return { ok: false, message: "true 또는 false여야 합니다." };
      return scalarValue(String(value), value);
    }
    case "null": {
      const normalized = text.trim().toLowerCase();
      if (normalized !== "" && normalized !== "null" && normalized !== "~") {
        return { ok: false, message: "빈 값이어야 합니다." };
      }
      return scalarValue("null", null);
    }
    case "date": {
      const raw = text.trim();
      const time = Date.parse(raw);
      if (raw === "" || Number.isNaN(time)) return { ok: false, message: "날짜 값이 아닙니다." };
      return scalarValue(new Date(time).toISOString(), raw);
    }
    default:
      return {
        ok: true,
        canonical: JSON.stringify(text),
        render: (key) => stringify({ [key]: text }, SCHEMA_OPTIONS),
      };
  }
}

function parseFieldValue(attrs: FrontMatterFieldAttrs, text: string): FieldValueResult {
  if (attrs.valueKind === "scalar") {
    return parseScalarText(attrs.scalarType ?? "string", text);
  }
  if (attrs.valueKind === "array") {
    const items = parseFlowSequence(text);
    if (items === null) return { ok: false, message: "단순 배열 YAML이 아닙니다." };
    return {
      ok: true,
      canonical: JSON.stringify(items),
      render: (key) => stringify({ [key]: items }, { ...SCHEMA_OPTIONS, collectionStyle: "flow" }),
    };
  }
  const document = parseDocument(`${renderKey(attrs.key)}:${text}`, PARSE_OPTIONS);
  if (document.errors.length > 0) return { ok: false, message: "YAML 문법 오류입니다." };
  return {
    ok: true,
    canonical: text,
    render: (key) => `${renderKey(key)}:${text}\n`,
  };
}

/** 필요한 경우 따옴표가 붙은 YAML 키 표현을 얻는다. */
function renderKey(key: string): string {
  const rendered = stringify({ [key]: null });
  const separator = rendered.lastIndexOf(":");
  return separator === -1 ? key : rendered.slice(0, separator);
}

/** 유효하지 않은 값도 "수정 여부"를 비교할 수 있도록 원문에 붙이는 표식. */
const RAW_CANONICAL_PREFIX = "raw:";

function canonicalValue(attrs: FrontMatterFieldAttrs, text: string): string {
  const parsed = parseFieldValue(attrs, text);
  return parsed.ok ? parsed.canonical : `${RAW_CANONICAL_PREFIX}${text}`;
}

function isValidKey(key: string): boolean {
  return key.length > 0 && key.trim() === key && !/[\r\n]/.test(key) && !key.startsWith("#");
}

function fieldAttrs(field: JSONContent): FrontMatterFieldAttrs {
  const attrs = (field.attrs ?? {}) as Partial<FrontMatterFieldAttrs>;
  return {
    key: attrs.key ?? "",
    valueKind: attrs.valueKind ?? "scalar",
    scalarType: attrs.scalarType ?? null,
    originalKey: attrs.originalKey ?? "",
    originalCanonicalValue: attrs.originalCanonicalValue ?? "",
    originalSource: attrs.originalSource ?? "",
  };
}

function fieldJsonFromPair(
  source: string,
  pair: ParsedPair,
  previousEnd: number
): { field: JSONContent; end: number } | null {
  if (!isScalar(pair.key) || typeof pair.key.value !== "string") return null;
  const keyRange = pair.key.range;
  if (!keyRange) return null;

  const valueEnd = pairValueEnd(pair);
  const valueRange = pair.value?.range;
  const rawValueSource = valueRange ? source.slice(valueRange[0], valueRange[1]) : "";
  const { valueKind, scalarType } = classifyValue(pair.value, rawValueSource);
  const end = lineEnd(source, Math.max(valueEnd, keyRange[1]));
  const start = fieldSourceStart(source, keyRange[0], previousEnd);
  const text = initialFieldText(source, pair, valueKind, end);
  const key = pair.key.value;
  const attrs: FrontMatterFieldAttrs = {
    key,
    valueKind,
    scalarType,
    originalKey: key,
    originalCanonicalValue: "",
    originalSource: source.slice(start, end),
  };
  attrs.originalCanonicalValue = canonicalValue(attrs, text);

  return {
    field: {
      type: FRONT_MATTER_FIELD_NODE_NAME,
      attrs: { ...attrs },
      content: text.length > 0 ? [{ type: "text", text }] : [],
    },
    end,
  };
}

/** 들여쓰지 않은 `key: value` 줄. 키에 콜론이 없는 단순한 형태만 받는다. */
const TOP_LEVEL_PAIR_LINE = /^([^\s#][^:]*):[ \t]+(\S.*?)[ \t]*$/;
/** 따옴표·flow·block·anchor·tag로 시작하는 값은 이미 명시적 표기이므로 손대지 않는다. */
const EXPLICIT_VALUE_START = /^["'[{|>&*!]/;

/**
 * 따옴표 없는 값 안의 `: `를 YAML은 중첩 매핑의 시작으로 읽어 문서 전체를 오류로 만든다.
 * `title: 회의록: 어쩌고`처럼 사람이 쓴 front matter에서 흔한 형태라, 최상위 `key: value`
 * 줄에 한해 값을 문자열로 간주하고 따옴표를 씌운 원문을 만든다.
 *
 * 들여쓴 줄은 건드리지 않는다. block scalar 본문과 여러 줄 값은 항상 들여쓰기되므로,
 * 그 안의 콜론을 값 구분자로 오인해 원문을 깨뜨리는 일이 없다.
 * 고칠 줄이 하나도 없으면 `null`을 돌려주어 호출자가 재파싱을 건너뛴다.
 */
function quoteAmbiguousPlainValues(yaml: string): string | null {
  let changed = false;
  const lines = yaml.split("\n").map((line) => {
    const match = TOP_LEVEL_PAIR_LINE.exec(line);
    if (!match) return line;
    const [, key, value] = match;
    if (EXPLICIT_VALUE_START.test(value) || !/:(?:\s|$)/.test(value)) return line;
    changed = true;
    return `${key}: ${stringify(value, SCHEMA_OPTIONS).trim()}`;
  });
  return changed ? lines.join("\n") : null;
}

/**
 * 문서 선두의 유효한 YAML front matter를 공유 필드 모델로 변환한다.
 * 경계 누락, YAML 오류, 중복 키, 최상위 sequence/scalar는 부분 변환 없이 `null`을 반환한다.
 * 단, 따옴표 없는 값의 콜론만이 원인인 오류는 그 값을 문자열로 읽어 한 번 더 시도한다.
 */
export function parseFrontMatter(markdown: string): FrontMatterParseResult | null {
  const slice = frontMatterSlice(markdown);
  if (!slice) return null;

  let source = slice.yaml;
  let document = parseDocument(source, PARSE_OPTIONS);
  if (document.errors.length > 0) {
    const repaired = quoteAmbiguousPlainValues(source);
    if (repaired === null) return null;
    const retried = parseDocument(repaired, PARSE_OPTIONS);
    if (retried.errors.length > 0) return null;
    source = repaired;
    document = retried;
  }
  if (document.contents !== null && !isMap(document.contents)) return null;

  const fields: JSONContent[] = [];
  let previousEnd = 0;
  for (const pair of document.contents?.items ?? []) {
    const built = fieldJsonFromPair(source, pair as ParsedPair, previousEnd);
    if (!built) return null;
    fields.push(built.field);
    previousEnd = built.end;
  }

  const attrs: FrontMatterAttrs = { trailingSource: source.slice(previousEnd) };
  return {
    frontMatter: { type: FRONT_MATTER_NODE_NAME, attrs: { ...attrs }, content: fields },
    body: slice.body,
  };
}

interface FieldState {
  attrs: FrontMatterFieldAttrs;
  text: string;
  parsed: FieldValueResult;
}

/**
 * 값 파싱은 필드마다 한 번만 수행하고 검증·직렬화가 그 결과를 함께 쓴다.
 * 카드 하나를 내보낼 때 같은 YAML을 세 번 다시 파싱하지 않기 위함이다.
 */
function fieldStates(node: JSONContent): FieldState[] {
  return (node.content ?? []).map((field) => {
    const attrs = fieldAttrs(field);
    const text = fieldText(field);
    return { attrs, text, parsed: parseFieldValue(attrs, text) };
  });
}

/** 카드 노드 전체의 중복 키와 필드별 키/값 오류를 계산한다. */
function collectErrors(states: FieldState[]): FrontMatterFieldError[] {
  const errors: FrontMatterFieldError[] = [];
  const seen = new Set<string>();
  states.forEach(({ attrs, parsed }, fieldIndex) => {
    if (seen.has(attrs.key)) {
      errors.push({ fieldIndex, code: "duplicate-key", message: "중복된 키입니다." });
    } else {
      seen.add(attrs.key);
    }
    if (!isValidKey(attrs.key)) {
      errors.push({ fieldIndex, code: "invalid-key", message: "사용할 수 없는 키입니다." });
    }
    if (!parsed.ok) {
      errors.push({ fieldIndex, code: "invalid-value", message: parsed.message });
    }
  });
  return errors;
}

export function validateFrontMatter(node: JSONContent): FrontMatterValidation {
  const errors = collectErrors(fieldStates(node));
  return { valid: errors.length === 0, errors };
}

function serializeField({ attrs, text, parsed }: FieldState): string {
  const canonical = parsed.ok ? parsed.canonical : `${RAW_CANONICAL_PREFIX}${text}`;
  const unchanged =
    attrs.originalSource !== "" &&
    attrs.key === attrs.originalKey &&
    canonical === attrs.originalCanonicalValue;
  if (unchanged) return attrs.originalSource;

  if (!isValidKey(attrs.key) || !parsed.ok) {
    return attrs.valueKind === "complex"
      ? `${attrs.key}:${text}\n`
      : `${attrs.key}: ${text}\n`.replace(/\n+$/, "\n");
  }
  return parsed.render(attrs.key);
}

/**
 * 수정하지 않은 필드는 원문 그대로, 수정한 유효 필드는 표준 YAML로,
 * 유효하지 않은 필드는 현재 원문을 버리지 않고 출력한다.
 */
export function serializeFrontMatter(node: JSONContent): FrontMatterSerialization {
  const states = fieldStates(node);
  const errors = collectErrors(states);
  const fields = states.map(serializeField).join("");
  const trailingSource = String(node.attrs?.trailingSource ?? "");
  return { yaml: fields + trailingSource, valid: errors.length === 0, errors };
}
